import { useAuth } from "common/hooks/useAuth";

import { useEffect, useId, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import BlockFormContainer, {
  BlockFormFieldsContainer,
  BlockFormTitleContainer,
} from "layouts/BlockFormLayout";

import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Breadcrumbs } from "@mui/material";
import axios from "axios";
import CustomBreadcrumb from "common/components/CustomBreadcrumb";
import LoadingSpinner from "common/components/LoadingSpinner";
import LemmasFileForm from "common/containers/Forms/UploadFile/LemmasFileForm";
import { BACKEND_URL_CONFIG } from "config";
import { ERRORS } from "errors";
import MainLayout from "layouts/MainLayout";
import { Fragment } from "react";
import { toast } from "react-toastify";
import styles from "./EditLemmasFilePage.module.css";

export default function EditLemmasFilePage() {
  // Contexts
  const { currentUser } = useAuth();
  const { lemmaFileId } = useParams();
  const navigate = useNavigate();

  // States
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(null);
  const [lemmaFileInformation, setLemmaFileInformation] = useState(null);

  // Refs
  const formIdRef = useId();

  useEffect(() => {
    const controller = new AbortController();
    fetchLemmaFileInformation(controller);
    return () => controller.abort();
  }, []);

  const fetchLemmaFileInformation = async (controller) => {
    setLoading(true);
    const url = BACKEND_URL_CONFIG.getLemmaFileInformation.replace(
      "{lemmaFileId}",
      lemmaFileId
    );
    try {
      const response = await axios.get(url, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
        signal: controller.signal,
      });
      if (response.status === 200) {
        setLemmaFileInformation(response.data);
      }
    } catch (error) {
      if (error.response.status === 404) {
        navigate("/lemmas");
        toast.error(ERRORS.error_182);
        return;
      }
      toast.error(ERRORS.error_164);
    } finally {
      setLoading(false);
    }
  };

  const saveLemmaFile = async (controller) => {
    const payload = {
      name: formData.file_label,
      is_certified: formData.is_certified,
    };
    updateLemmaFile(payload, controller);
  };

  const updateLemmaFile = async (payload, controller) => {
    setLoading(true);
    try {
      const url = BACKEND_URL_CONFIG.updateLemmasFile.replace(
        "{lemmaFileId}",
        lemmaFileId
      );
      const response = await axios.put(url, payload, {
        headers: {
          "X-KEY": currentUser.accessToken,
        },
        signal: controller.signal,
      });
      if (response.status === 200) {
        toast.success("Votre fichier de lemmes a été modifié");
        navigate("/lemmas");
      }
    } catch (error) {
      if (error.response.status === 409) {
        toast.error(ERRORS.error_171);
      } else {
        toast.error(ERRORS.error_163);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    if (formData) {
      saveLemmaFile(controller);
    }
    return () => controller.abort();
  }, [formData]);

  return (
    <Fragment>
      <MainLayout>
        <div className={styles.container}>
          <div className={styles["breadcrumbs-wrapper"]}>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <CustomBreadcrumb
                component="a"
                href="/"
                label="Page d'accueil"
                icon={<FontAwesomeIcon icon={faHome} />}
              />
              <CustomBreadcrumb component="a" href="/lemmas" label="Lemmes" />
              <CustomBreadcrumb component="a" href="#" label="Modifier" />
            </Breadcrumbs>
          </div>
          <div className={styles["main-section"]}>
            <BlockFormContainer>
              <BlockFormTitleContainer
                title="Modifier un fichier de lemmes"
                formId={formIdRef}
                loading={loading}
                isSubmitting={isSubmitting}
              />
              <BlockFormFieldsContainer>
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <LemmasFileForm
                    lemmaFileInformation={lemmaFileInformation}
                    setFormData={setFormData}
                    formId={formIdRef}
                    setIsSubmitting={setIsSubmitting}
                  />
                )}
              </BlockFormFieldsContainer>
            </BlockFormContainer>
          </div>
        </div>
      </MainLayout>
    </Fragment>
  );
}
